import {
  GameState,
  WorldPoint,
  DecisionChoice,
  Footprint,
  DummyWalkerNPC,
  InspectorNPC,
  GamePhase,
  MapId,
  DogBreed,
} from '../domain/types.ts';
import { GAME_CONFIG } from '../config/gameConfig.ts';
import { createNeighborhoodMap, createInitialDummyWalkers } from '../map/mapData.ts';
import { checkSurveillance } from '../logic/visibility.ts';
import { Haptics } from '../utils/haptics.ts';
import { findRoadPath, findOrthogonalRoadPath, snapToRoad } from '../logic/roadNavigation.ts';
import { DialogService, calcReadingDuration } from '../services/dialogService.ts';
import { KVService } from '../services/kvService.ts';
import { canSeeTarget } from '../logic/vision.ts';
import { handleNpcWitnessAction } from '../entities/npc.ts';
import { SpeechCoordinator } from './speechCoordinator.ts';

export class GameEngine {
  private state: GameState;
  private lastTime = performance.now();
  private onStateChange?: (state: Readonly<GameState>) => void;
  private onCaught?: (details: NonNullable<GameState['caughtEvent']>) => void;
  private lastBreadcrumbPos = { x: -40, y: 0 };
  private speechCoordinator = new SpeechCoordinator();

  constructor() {
    const map = createNeighborhoodMap();

    this.state = {
      phase: 'BOOT',
      selectedMap: 'cau-giay',
      selectedBreed: 'corgi',
      playerName: 'Dũng Corgi',
      player: {
        x: -40,
        y: 0,
        facingAngle: 0,
        speed: GAME_CONFIG.resources.moveSpeed,
        isEntangled: false,
        entangledTimer: 0,
        isCleaning: false,
        cleanTimer: 0,
        targetMove: null,
        waypoints: [],
      },
      dog: {
        x: -42,
        y: 0,
        facingAngle: 0,
        bowels: 15,
        stress: 0,
        isPooping: false,
        poopTimer: 0,
        isBarking: false,
        barkTimer: 0,
        isAnchor: false,
        breadcrumbs: [{ x: -40, y: 0 }],
      },
      reputation: GAME_CONFIG.resources.initialReputation,
      bagsCount: GAME_CONFIG.resources.maxBagsPerWalk,
      day: 1,

      cooperateCount: 0,
      defectCount: 0,
      blameCount: 0,
      caughtCount: 0,

      poops: [],
      dummyWalkers: createInitialDummyWalkers(),
      paranoia: 10, // Khởi đầu ngõ phố bình yên mức 10%
      cameras: map.cameras,
      inspectors: map.inspectors,
      teaStalls: map.teaStalls,
      chiliTraps: map.chiliTraps,
      wasteBaskets: map.wasteBaskets,
      trees: map.trees,
      buildings: map.buildings,
      roads: map.roads,
      footprints: [],
      soundWaves: [],

      activeDilemma: null,
      qteKick: {
        active: false,
        power: 0,
        cursorPos: 0,
        speed: GAME_CONFIG.gameTheory.qte.cursorSpeed,
        sweetSpotStart: GAME_CONFIG.gameTheory.qte.sweetSpotStart,
        sweetSpotEnd: GAME_CONFIG.gameTheory.qte.sweetSpotEnd,
        targetAngle: 0,
        targetDoorPos: { x: 38, y: -26 }, // Cổng nhà nuôi chó hoang
      },
      caughtEvent: null,
      toastMessage: {
        text: 'Dắt chó đi dạo! Chú ý các camera và quán nước!',
        type: 'info',
        duration: 4000,
      },
      gameMasterComment: {
        theoryTopic: 'Spatial Game Theory',
        text: 'Chào mừng! Không gian vỉa hè là ma trận thông tin bất cân xứng: dọn (Cooperate) hay chuồn (Defect)?',
        timer: 7000,
      },
    };
  }

  public getState(): Readonly<GameState> {
    return this.state;
  }

  public setReputation(val: number): void {
    this.state.reputation = val;
  }

  public setOnStateChange(cb: (state: Readonly<GameState>) => void): void {
    this.onStateChange = cb;
  }

  public setOnCaught(cb: (details: NonNullable<GameState['caughtEvent']>) => void): void {
    this.onCaught = cb;
  }

  public setPlayerTarget(target: WorldPoint): void {
    if (this.state.player.isEntangled || this.state.player.isCleaning) return;

    // 1. Nếu người chơi đang bị lệch khỏi lòng đường (> 1.0m), snap ngay về tim đường gần nhất
    const currentSnap = snapToRoad({ x: this.state.player.x, y: this.state.player.y }, this.state.roads);
    if (currentSnap.dist > 1.0) {
      this.state.player.x = currentSnap.snapped.x;
      this.state.player.y = currentSnap.snapped.y;
    }

    // 2. Snap điểm click vào tim đường gần nhất
    const targetSnap = snapToRoad(target, this.state.roads);

    // 3. Tìm lộ trình A* bám 100% trên mạng lưới đường, không đi chéo qua đất trống
    const path = findRoadPath(
      { x: this.state.player.x, y: this.state.player.y },
      targetSnap.snapped,
      this.state.roads
    );
    this.state.player.waypoints = path;
    this.state.player.targetMove = path[0] ?? null;
  }

  public update(): void {
    const now = performance.now();
    const dt = Math.min(0.1, (now - this.lastTime) / 1000);
    this.lastTime = now;

    this.updatePlayer(dt);
    this.updateDog(dt);
    this.speechCoordinator.update(dt);
    this.updateDummyWalkers(dt);
    this.updateParanoia(dt);
    this.checkChickenGame(dt);
    this.updateInspectors(dt);
    this.updateTeaStalls(dt);
    this.updateCameras(dt);
    this.updateDilemma(dt);
    this.updateQTE(dt);
    this.updateGameMaster(dt);
    this.updateSoundWaves(dt);
    this.updateToasts(dt);

    if (this.onStateChange) {
      this.onStateChange(this.state);
    }
  }

  // Player movement is controlled via setPlayerTarget (orthogonal waypoints snap to roads)

  private updatePlayer(dt: number): void {
    const p = this.state.player;

    // Trạng thái trói chân do chó ngáo quấn xích
    if (p.isEntangled) {
      p.entangledTimer -= dt * 1000;
      if (p.entangledTimer <= 0) {
        p.isEntangled = false;
        this.showToast('Đã gỡ xong dây xích rối!', 'info');
      }
      return;
    }

    // Đang đứng dọn phân
    if (p.isCleaning) {
      p.cleanTimer -= dt * 1000;
      if (p.cleanTimer <= 0) {
        p.isCleaning = false;
        this.finishCleaning();
      }
      return;
    }
    // Di chuyển theo các Waypoint dọc tuyến đường (90 độ, KHÔNG ĐI CHÉO)
    if (p.targetMove) {
      const dx = p.targetMove.x - p.x;
      const dy = p.targetMove.y - p.y;
      const dist = Math.hypot(dx, dy);

      if (dist > 0.4) {
        p.facingAngle = Math.atan2(dy, dx);
        const moveDist = Math.min(dist, p.speed * dt);
        p.x += (dx / dist) * moveDist;
        p.y += (dy / dist) * moveDist;

        // Lưu vết chân cho con chó bám theo (mỗi 0.4m)
        const distFromLastBreadcrumb = Math.hypot(
          p.x - this.lastBreadcrumbPos.x,
          p.y - this.lastBreadcrumbPos.y
        );
        if (distFromLastBreadcrumb > 0.4) {
          this.state.dog.breadcrumbs.push({ x: p.x, y: p.y });
          if (this.state.dog.breadcrumbs.length > 20) {
            this.state.dog.breadcrumbs.shift();
          }
          this.lastBreadcrumbPos = { x: p.x, y: p.y };
        }

        // Nếu dính chân vào phân cũ (Boomerang effect)
        this.checkPoopCollision(p.x, p.y);
      } else {
        // Đã đến waypoint hiện tại -> Chuyển sang waypoint tiếp theo dọc đường
        p.waypoints.shift();
        if (p.waypoints.length > 0) {
          p.targetMove = p.waypoints[0]!;
        } else {
          p.targetMove = null;
        }
      }
    }
  }

  private updateDog(dt: number): void {
    const d = this.state.dog;
    const p = this.state.player;

    // 1. Kiểm tra va chạm Bẫy Ớt Bột
    if (!p.isEntangled) {
      for (const trap of this.state.chiliTraps) {
        const distToTrap = Math.hypot(d.x - trap.x, d.y - trap.y);
        if (distToTrap <= trap.radius) {
          // Kích hoạt Entanglement! Chó hắt xì, chạy vòng quanh quấn xích vào chân chủ
          p.isEntangled = true;
          p.entangledTimer = GAME_CONFIG.dog.entanglementDurationMs;
          p.targetMove = null;
          Haptics.alertCaught();
          this.showToast(
            '🌶️ Chó hít ớt bột hắt xì! Dây xích quấn chặt cứng vào chân bạn!',
            'danger'
          );
          break;
        }
      }
    }

    // 2. Chó đang xả phân (Anchor State)
    if (d.isPooping) {
      d.poopTimer -= dt * 1000;

      // Nếu chủ cố kéo xích đi xa lúc chó đang xả -> Căng thẳng tăng vọt
      const leashDist = Math.hypot(d.x - p.x, d.y - p.y);
      if (leashDist > GAME_CONFIG.dog.maxLeashLength * 0.8) {
        d.stress = Math.min(100, d.stress + GAME_CONFIG.dog.leashStressRate * dt);
        Haptics.leashTension();
        if (d.stress >= 100 && !d.isBarking) {
          d.isBarking = true;
          d.barkTimer = 3000;
          this.alertNearbyInspectors(d.x, d.y);
          Haptics.alertCaught();
          this.showToast('🚨 Chó bị kéo đau sủa ầm ĩ! Bác tổ trưởng đã chú ý!', 'danger');
        }
      }

      if (d.poopTimer <= 0) {
        d.isPooping = false;
        d.isAnchor = false;
        d.bowels = 0;
        this.dropPoop(d.x, d.y);
      }
      return;
    }

    // 3. Cập nhật sủa inh ỏi
    if (d.isBarking) {
      d.barkTimer -= dt * 1000;
      if (d.barkTimer <= 0) d.isBarking = false;
    }

    // 4. Vật lý dây xích bám theo dấu chân của chủ (Bo cua vuông góc 90 độ, KHÔNG ĐI CHÉO)
    let targetFollowPos = { x: p.x, y: p.y };
    if (d.breadcrumbs.length > 0) {
      targetFollowPos = d.breadcrumbs[0]!;
    }

    const dx = targetFollowPos.x - d.x;
    const dy = targetFollowPos.y - d.y;
    const distToTarget = Math.hypot(dx, dy);
    const distToPlayer = Math.hypot(p.x - d.x, p.y - d.y);

    if (distToPlayer > 1.0) {
      // Hướng nhìn theo điểm dấu chân tiếp theo
      d.facingAngle = Math.atan2(dy, dx);
      let targetSpeed = Math.max(GAME_CONFIG.dog.walkSpeed, p.speed * (distToPlayer / 1.3));

      // Nếu mót > 90% thì đi chậm lại, ghì xích
      if (d.bowels >= GAME_CONFIG.dog.criticalBowelsThreshold) {
        targetSpeed *= 0.6;
      }

      const moveStep = Math.min(distToTarget, targetSpeed * dt);
      if (moveStep > 0 && distToTarget > 0.05) {
        d.x += (dx / distToTarget) * moveStep;
        d.y += (dy / distToTarget) * moveStep;

        // Mót ruột tăng dần theo quãng đường
        d.bowels = Math.min(100, d.bowels + moveStep * GAME_CONFIG.dog.bowelsIncreasePerMeter);
      }

      // Khi chó đã tiến sát điểm dấu chân (< 0.5m), chuyển sang dấu chân tiếp theo
      if (d.breadcrumbs.length > 0 && distToTarget < 0.5) {
        d.breadcrumbs.shift();
      }
    }

    // 5. Kiểm tra nếu Mót Ruột chạm 100% -> Chó đóng đinh xả phân tại chỗ!
    if (d.bowels >= 100 && !d.isPooping) {
      d.isPooping = true;
      d.isAnchor = true;
      d.poopTimer = GAME_CONFIG.dog.poopDurationMs;
      Haptics.dogPoopSquat();
      this.showToast('🐶 Con chó không nhịn được nữa, ngồi thụp xuống xả!', 'warning');
    }
  }

  private dropPoop(x: number, y: number): void {
    const newPoop = {
      id: `poop-${Date.now()}`,
      x,
      y,
      createdAt: Date.now(),
      ownerIsPlayer: true,
      isCleaned: false,
      isBlamed: false,
      escapeTimerMs: 5000, // Có đúng 5.0 giây để tẩu thoát
      isEscaped: false,
      dangerRadius: 14.0, // Phải chạy xa tối thiểu 14 mét
    };
    this.state.poops.push(newPoop);

    // Kích hoạt Countdown 3 giây chọn phương án Game Theory!
    this.state.activeDilemma = {
      active: true,
      poopPos: { x, y },
      remainingTimeMs: 3000,
      selectedChoice: null,
    };
  }

  private updateDilemma(dt: number): void {
    const dil = this.state.activeDilemma;
    if (!dil || !dil.active) return;

    dil.remainingTimeMs -= dt * 1000;

    // Hết 3 giây không chọn gì -> Mặc định là Chuồn Lẹ (Defect)
    if (dil.remainingTimeMs <= 0) {
      this.selectDilemmaChoice('defect');
    }
  }

  public selectDilemmaChoice(choice: DecisionChoice): void {
    const dil = this.state.activeDilemma;
    if (!dil) return;
    dil.active = false;
    dil.selectedChoice = choice;

    if (choice === 'cooperate') {
      this.handleCooperate(dil.poopPos);
    } else if (choice === 'defect') {
      this.handleDefect(dil.poopPos);
    } else if (choice === 'blame_shift') {
      this.handleBlameShift(dil.poopPos);
    }
  }

  private handleCooperate(poopPos: WorldPoint): void {
    this.state.cooperateCount++;

    if (this.state.bagsCount <= 0) {
      this.showToast('❌ Hết túi nilon rồi! Không dọn được, buộc phải chuồn!', 'danger');
      this.handleDefect(poopPos);
      return;
    }

    // Bắt đầu 3 giây đứng dọn
    this.state.player.isCleaning = true;
    this.state.player.cleanTimer = GAME_CONFIG.resources.cleanTimeMs;
    this.state.bagsCount--;

    // Kiểm tra có ở gần Quán Trà Đá không?
    for (const stall of this.state.teaStalls) {
      const dist = Math.hypot(poopPos.x - stall.x, poopPos.y - stall.y);
      if (dist <= stall.radius) {
        // Quán trà đá cà khịa dù có dọn -> Trừ 5 điểm tự ái!
        this.state.reputation = Math.max(0, this.state.reputation - GAME_CONFIG.gameTheory.quannuocSelfEsteemPenalty);
        this.showToast(
          '☕ Cô bán trà đá bĩu môi: "Gớm, dắt chó đi ỉa mà cũng bày đặt mang túi, hôi rình!" (-5 Tự Ái)',
          'warning'
        );
        return;
      }
    }

    // Tương tác phản ứng của dàn Dummy NPC nếu đứng gần (< 14m) qua SpeechCoordinator
    const nearbyWitnesses = this.state.dummyWalkers.filter(
      (w) => Math.hypot(poopPos.x - w.x, poopPos.y - w.y) <= 14
    );
    if (nearbyWitnesses.length > 0) {
      const speaker = this.speechCoordinator.electAndSpeakGroup(
        nearbyWitnesses,
        'cooperate',
        poopPos,
        this.state.dummyWalkers,
        this.state.inspectors
      );
      if (speaker) {
        if (speaker.archetype === 'altruist') {
          this.state.reputation = Math.min(100, this.state.reputation + 2);
          this.showToast('🌸 +2 Điểm Thảo Mai từ Thánh Mẫu!', 'success');
        } else if (speaker.archetype === 'tit_for_tat') {
          speaker.hasWitnessedPlayerDefect = false;
        }
      }
    }

    this.showToast('🧹 Đang cúi xuống hốt phân... (Mất 1 túi nilon)', 'info');
  }

  private finishCleaning(): void {
    const latestPoop = this.state.poops[this.state.poops.length - 1];
    if (latestPoop) {
      latestPoop.isCleaned = true;
    }
    this.state.reputation = Math.min(100, this.state.reputation + GAME_CONFIG.gameTheory.repGainClean);
    this.showToast('✨ Dọn sạch bóng! +5 Điểm Công Dân gương mẫu!', 'success');
  }

  private handleDefect(poopPos: WorldPoint): void {
    this.state.defectCount++;
    this.state.paranoia = Math.min(100, this.state.paranoia + 15);
    this.showToast('💨 CHUỒN LẸ! Phải chạy xa >14m trong vòng 5 giây trước khi bị tóm!', 'warning');

    // 1. Kiểm tra Thánh Mẫu Đạo Đức (Bảo Ngọc) - Camera di động 100% bắt quả tang!
    const altruist = this.state.dummyWalkers.find((w) => w.archetype === 'altruist');
    if (altruist) {
      const distToAltruist = Math.hypot(poopPos.x - altruist.x, poopPos.y - altruist.y);
      if (distToAltruist <= GAME_CONFIG.gameTheory.dummyWalkers.altruistVisionRange) {
        altruist.speechText = 'Ối dồi ôi, tính chuồn hả con vợ!';
        altruist.speechTimer = 4000;
        altruist.speechColor = '#ff1744';
        this.triggerCaught(
          'Bảo Ngọc (Thánh Mẫu) bắt quả tang bạn chuồn lẹ không dọn phân!',
          'Bảo Ngọc — Người Hàng Xóm Yêu Môi Trường',
          3.5
        );
        return;
      }
    }

    // 2. Kiểm tra Tit-for-Tat (Hùng Còi) - Nhớ mặt để trả đũa sau!
    const titForTat = this.state.dummyWalkers.find((w) => w.archetype === 'tit_for_tat');
    if (titForTat) {
      const distToTFT = Math.hypot(poopPos.x - titForTat.x, poopPos.y - titForTat.y);
      if (distToTFT <= 14) {
        titForTat.hasWitnessedPlayerDefect = true;
        titForTat.isSeekingRevenge = true;
        titForTat.speechText = 'Tao ghim mày rồi đấy, đợi đấy con vợ!';
        titForTat.speechTimer = 4000;
        titForTat.speechColor = '#ffd600';
        this.showToast('👀 Hùng Còi (Tit-for-Tat) đã ghim mặt bạn! Sắp có bãi mìn trước cửa nhà!', 'danger');
      }
    }

    // 3. Kiểm tra tầm nhìn LoS Camera & Bác tổ trưởng
    const check = checkSurveillance(
      poopPos,
      this.state.cameras,
      this.state.inspectors,
      this.state.teaStalls,
      this.state.buildings
    );

    if (check.isCaught) {
      this.triggerCaught(check.reason || 'Bị bắt tại trận!', check.watcherName || 'Camera', check.disgraceMultiplier);
    } else {
      this.showToast('🎉 Thoát nạn trong gang tấc! Không ma nào để ý!', 'success');
    }
  }

  private handleBlameShift(poopPos: WorldPoint): void {
    this.state.blameCount++;
    // Bật mini game QTE Căn lực sút!
    this.state.qteKick.active = true;
    this.state.qteKick.cursorPos = 0;
    this.showToast('⚽ Bấm CÁCH hoặc nút SÚT để căn lực gạt bãi phân sang nhà bên!', 'warning');
  }

  public executeQTEKick(): void {
    const qte = this.state.qteKick;
    if (!qte.active) return;
    qte.active = false;

    const hit =
      qte.cursorPos >= qte.sweetSpotStart &&
      qte.cursorPos <= qte.sweetSpotEnd;

    const latestPoop = this.state.poops[this.state.poops.length - 1];

    if (hit) {
      // Sút chuẩn: Bãi phân bay sang cổng nhà nuôi chó hoang!
      Haptics.qteSuccess();
      if (latestPoop) {
        latestPoop.x = qte.targetDoorPos.x;
        latestPoop.y = qte.targetDoorPos.y;
        latestPoop.isBlamed = true;
      }
      this.showToast(
        '🎯 SÚT ĐẲNG CẤP! Bãi mìn bay thẳng sang mép cổng nhà nuôi chó hoang! Thoát nạn!',
        'success'
      );
    } else {
      // Sút trượt: Giày dính trọn bãi phân, để lại vệt dấu chân về nhà
      Haptics.qteFailed();
      this.spawnFootprintTrail();
      this.triggerCaught(
        'Lấy chân gạt phân sút bóng hụt, giày dính trọn bãi mìn để lại dấu vết tận cửa!',
        'Camera Tiệm Vàng 4K',
        GAME_CONFIG.gameTheory.disgraceMultiplier.blameFailed
      );
    }
  }

  private spawnFootprintTrail(): void {
    const p = this.state.player;
    for (let i = 0; i < 8; i++) {
      this.state.footprints.push({
        x: p.x - i * 1.5,
        y: p.y,
        angle: 0,
        opacity: Math.max(0.2, 1.0 - i * 0.1),
      });
    }
  }

  private triggerCaught(reason: string, caughtBy: string, mult: number): void {
    this.state.caughtCount++;
    Haptics.alertCaught();
    const fine = Math.round(GAME_CONFIG.gameTheory.baseFineCaught * mult);
    this.state.reputation = Math.max(0, this.state.reputation - fine);

    const caughtEv = {
      reason,
      headline: `[HỘI CƯ DÂN NGÕ 165 CẦU GIẤY] TÌM CHỦ CHÓ VÔ Ý THỨC!`,
      disgraceMultiplier: mult,
      caughtBy,
    };
    this.state.caughtEvent = caughtEv;

    if (this.onCaught) {
      this.onCaught(caughtEv);
    }
  }

  private updateQTE(dt: number): void {
    const qte = this.state.qteKick;
    if (!qte.active) return;

    qte.cursorPos += qte.speed * dt;
    if (qte.cursorPos > 1) {
      qte.cursorPos = 1;
      qte.speed = -Math.abs(qte.speed);
    } else if (qte.cursorPos < 0) {
      qte.cursorPos = 0;
      qte.speed = Math.abs(qte.speed);
    }
  }

  private updateInspectors(dt: number): void {
    for (const npc of this.state.inspectors) {
      if (npc.speechTimer && npc.speechTimer > 0) {
        npc.speechTimer -= dt * 1000;
        if (npc.speechTimer <= 0) {
          npc.speechText = undefined;
        }
      }

      const targetPoint = npc.patrolPoints[npc.currentPointIndex]!;
      const dx = targetPoint.x - npc.x;
      const dy = targetPoint.y - npc.y;
      const dist = Math.hypot(dx, dy);

      if (dist < 0.5) {
        npc.currentPointIndex =
          (npc.currentPointIndex + 1) % npc.patrolPoints.length;
      } else {
        npc.facingAngle = Math.atan2(dy, dx);
        npc.x += (dx / dist) * npc.speed * dt;
        npc.y += (dy / dist) * npc.speed * dt;
      }
    }
  }

  private updateTeaStalls(dt: number): void {
    const player = this.state.player;

    for (const stall of this.state.teaStalls) {
      // 1. Giảm commentTimer: Khi hết giờ thì TẮT NGAY activeComment để bong bóng thoại biến mất!
      if (stall.commentTimer && stall.commentTimer > 0) {
        stall.commentTimer -= dt * 1000;
        if (stall.commentTimer <= 0) {
          stall.activeComment = undefined;
          stall.cooldownTimer = 7500; // Nghỉ 7.5s trước khi có thể nói câu tiếp
        }
      }

      // 2. Giảm cooldownTimer
      if (stall.cooldownTimer && stall.cooldownTimer > 0) {
        stall.cooldownTimer -= dt * 1000;
      }

      // 3. Khi người chơi bước vào bán kính radar quán nước mà quán đang không nói gì và hết cooldown
      const dist = Math.hypot(player.x - stall.x, player.y - stall.y);
      if (dist <= stall.radius && !stall.activeComment && (stall.cooldownTimer ?? 0) <= 0) {
        const line = DialogService.getInstance().getDialog('teastall_idle');
        stall.activeComment = line;
        stall.commentTimer = calcReadingDuration(line);
      }
    }
  }

  private updateCameras(dt: number): void {
    const now = performance.now();
    for (const cam of this.state.cameras) {
      cam.isBlinking = Math.floor(now / GAME_CONFIG.surveillance.camera.blinkIntervalMs) % 2 === 0;
    }
  }

  private alertNearbyInspectors(x: number, y: number): void {
    const hearingInspectors: InspectorNPC[] = [];
    for (const npc of this.state.inspectors) {
      const dist = Math.hypot(npc.x - x, npc.y - y);
      if (dist <= GAME_CONFIG.dog.barkAlertRadius) {
        npc.facingAngle = Math.atan2(y - npc.y, x - npc.x);
        hearingInspectors.push(npc);
      }
    }
    if (hearingInspectors.length > 0) {
      this.speechCoordinator.handleInspectorsBarkAlert(
        hearingInspectors,
        { x, y },
        () => DialogService.getInstance().getDialog('inspector_alert')
      );
    }
  }

  private checkPoopCollision(px: number, py: number): void {
    const now = Date.now();

    for (const poop of this.state.poops) {
      if (poop.isCleaned) continue;

      const dist = Math.hypot(px - poop.x, py - poop.y);

      // 1. Dẫm trúng phân: Tốc độ chạy giảm 50%
      if (dist < 1.0) {
        this.state.player.speed = GAME_CONFIG.resources.moveSpeed * GAME_CONFIG.gameTheory.ownPoopSpeedDebuff;
        this.showToast('💩 DẪM TRÚNG PHÂN! Tốc độ chạy giảm 50%!', 'danger');
      }

      // 2. Cơ chế: Xả xong nếu không chuồn sớm trong vòng 5 giây hoặc chưa cách xa bán kính 14 mét thì bị bắt!
      if (poop.ownerIsPlayer && !poop.isEscaped) {
        const escapeTimeLimit = poop.escapeTimerMs ?? 5000;
        const requiredRadius = poop.dangerRadius ?? 14.0;
        const elapsed = now - poop.createdAt;

        // Nếu đã chạy thoát ra ngoài bán kính an toàn trước khi hết giờ -> Thành công
        if (dist >= requiredRadius) {
          poop.isEscaped = true;
          this.showToast('💨 Đã tẩu thoát an toàn ra khỏi hiện trường!', 'success');
        } else if (elapsed > escapeTimeLimit) {
          // Hết thời gian mà vẫn lảng vảng trong vùng nguy hiểm (< 14m)
          const check = checkSurveillance(
            { x: poop.x, y: poop.y },
            this.state.cameras,
            this.state.inspectors,
            this.state.teaStalls,
            this.state.buildings
          );

          if (check.isCaught) {
            this.triggerCaught(
              `Chuồn quá chậm! Lảng vảng cạnh bãi phân sau ${Math.round(escapeTimeLimit / 1000)}s nên bị camera ghi trọn hình ảnh!`,
              check.watcherName || 'Camera An Ninh',
              3.0
            );
            return;
          }
        }
      }

      // 3. Cơ chế: Đi ngang bãi mìn của người khác xả -> Khả năng cao bị gán ghép là thủ phạm (Scapegoat)!
      if (!poop.ownerIsPlayer && !poop.isCleaned) {
        // Nếu người chơi bước vào vùng nghi can (dưới 4.5m cạnh bãi mìn của người khác)
        if (dist <= 4.5) {
          const check = checkSurveillance(
            { x: px, y: py },
            this.state.cameras,
            this.state.inspectors,
            this.state.teaStalls,
            this.state.buildings
          );

          // Nếu có Camera hoặc Tổ Trưởng nhìn thấy đúng lúc bạn đứng cạnh bãi phân của kẻ khác
          if (check.isCaught) {
            // Xác suất 80% bị xóm quy chụp là kẻ xả bậy (Tình ngay lý gian)
            if (Math.random() < 0.8) {
              this.triggerCaught(
                'Tình ngay lý gian! Đi ngang bãi phân của đứa khác đúng lúc Bác tổ trưởng nhìn thấy, bị cả xóm gán ghép là thủ phạm!',
                `${check.watcherName || 'Bác Tổ Trưởng'} (Bắt oan)`,
                2.5
              );
              return;
            } else {
              this.showToast('⚠️ Đi ngang bãi mìn của người khác! Coi chừng bị chụp mũ đổ vạ!', 'warning');
            }
          }
        }
      }
    }
  }

  private updateToasts(dt: number): void {
    if (this.state.toastMessage) {
      this.state.toastMessage.duration -= dt * 1000;
      if (this.state.toastMessage.duration <= 0) {
        this.state.toastMessage = null;
      }
    }
  }

  public showToast(text: string, type: 'info' | 'warning' | 'danger' | 'success'): void {
    this.state.toastMessage = { text, type, duration: calcReadingDuration(text) };
  }

  private updateGameMaster(dt: number): void {
    const gm = this.state.gameMasterComment;
    if (gm.timer > 0) {
      gm.timer -= dt * 1000;
      if (gm.timer <= 0) {
        this.rotateGameMasterIdleComment();
      }
    }
  }

  public setGameMasterComment(theoryTopic: string, text: string, durationMs?: number): void {
    this.state.gameMasterComment = {
      theoryTopic,
      text,
      timer: durationMs ?? Math.max(4500, calcReadingDuration(text) + 1200),
    };
  }

  private rotateGameMasterIdleComment(): void {
    const idleCommentary: Array<{ topic: string; text: string }> = [
      {
        topic: 'Payoff Matrix',
        text: 'Ma trận chi phí: Hợp tác tốn 1 túi rác nhưng giữ vững Uy tín; Bội phản tiết kiệm túi nhưng rủi ro bóc phốt cực nặng!',
      },
      {
        topic: 'Repeated Game (Folk Theorem)',
        text: 'Trò chơi lặp lại: Bạn đi dạo cùng một khu phố mỗi ngày. Uy tín tích lũy quyết định thái độ của cả xóm.',
      },
      {
        topic: 'Line of Sight & Asymmetric Information',
        text: 'Thông tin bất cân xứng: Chủ chó luôn tìm góc chết thị giác, trong khi camera và quán nước thiết lập radar giám sát 360°!',
      },
      {
        topic: 'Negative Externality',
        text: 'Ngoại tác tiêu cực: Bãi phân bỏ lại là chi phí ô nhiễm và phiền toái bị đẩy sang cho người đi bộ phía sau.',
      },
      {
        topic: 'Tragedy of the Commons',
        text: 'Bi kịch mảnh đất công: Nếu ai cũng chuồn lẹ để hưởng lợi cá nhân, cả vỉa hè ngõ phố sẽ ngập tràn bãi mìn!',
      },
    ];

    const item = idleCommentary[Math.floor(Math.random() * idleCommentary.length)]!;
    this.setGameMasterComment(item.topic, item.text, 8500);
  }

  public setPhase(phase: GamePhase): void {
    this.state.phase = phase;
  }

  public loadMap(mapId: MapId, breed: DogBreed = 'corgi', playerName = 'Dũng Corgi'): void {
    this.state.selectedMap = mapId;
    this.state.selectedBreed = breed;
    this.state.playerName = playerName;
    this.state.phase = 'PLAYING';

    const map = createNeighborhoodMap(mapId);
    this.state.roads = map.roads;
    this.state.buildings = map.buildings;
    this.state.trees = map.trees;
    this.state.wasteBaskets = map.wasteBaskets;
    this.state.cameras = map.cameras;
    this.state.inspectors = map.inspectors;
    this.state.teaStalls = map.teaStalls;
    this.state.chiliTraps = map.chiliTraps;
    this.state.dummyWalkers = createInitialDummyWalkers(mapId);
    this.state.poops = [];
    this.state.paranoia = 10;

    // Điều chỉnh tốc độ theo giống chó
    if (breed === 'corgi') {
      this.state.player.speed = 7.5;
    } else if (breed === 'poodle') {
      this.state.player.speed = 7.0;
    } else if (breed === 'native') {
      this.state.player.speed = 8.2;
    }

    this.resetRound();
    const mapLabel = mapId === 'cau-giay' ? 'Ngõ 165 Cầu Giấy' : mapId === 'ta-hien' ? 'Phố Cổ Tạ Hiện' : 'Bán Đảo Linh Đàm';
    this.setGameMasterComment(
      'Khởi Động Bản Đồ',
      `Chào mừng ${playerName} và cún cưng đến với ${mapLabel}! Hãy chú ý các góc nhìn giám sát!`,
      7000
    );
  }

  public loadRealOsmMap(
    realMap: import('../map/realMapLoader.ts').RealMapData,
    breed: DogBreed = 'corgi',
    playerName = 'Dũng Corgi',
    aiConditions?: import('../services/aiMapGenerator.ts').GeneratedConditions
  ): void {
    this.state.selectedBreed = breed;
    this.state.playerName = playerName;
    this.state.phase = 'PLAYING';

    this.state.roads = realMap.roads;
    this.state.buildings = realMap.buildings;
    this.state.trees = realMap.trees;
    this.state.wasteBaskets = realMap.wasteBaskets;
    this.state.cameras = realMap.cameras;
    this.state.inspectors = realMap.inspectors;
    this.state.teaStalls = realMap.teaStalls;
    this.state.chiliTraps = realMap.chiliTraps;
    this.state.dummyWalkers = realMap.dummyWalkers;
    this.state.poops = [];
    this.state.paranoia = aiConditions?.initialParanoia ?? 20;

    // Đặt vị trí xuất phát của người chơi tại tuyến đường gần tâm (0, 0) nhất
    const centerSnap = snapToRoad({ x: 0, y: 0 }, realMap.roads);
    const spawnPt = centerSnap.snapped;
    this.state.player.x = spawnPt.x;
    const dogSnap = snapToRoad({ x: spawnPt.x - 1.5, y: spawnPt.y }, realMap.roads);
    this.state.dog.x = dogSnap.snapped.x;
    this.state.dog.y = dogSnap.snapped.y;
    this.lastBreadcrumbPos = { x: spawnPt.x, y: spawnPt.y };
    this.state.dog.breadcrumbs = [{ x: dogSnap.snapped.x, y: dogSnap.snapped.y }];

    // Điều chỉnh tốc độ theo giống chó
    if (breed === 'corgi') {
      this.state.player.speed = 7.5;
    } else if (breed === 'poodle') {
      this.state.player.speed = 7.0;
    } else if (breed === 'native') {
      this.state.player.speed = 8.2;
    }

    this.resetRound();
    // Giữ lại vị trí spawn vừa đặt
    this.state.player.x = spawnPt.x;
    this.state.player.y = spawnPt.y;
    this.state.dog.x = spawnPt.x - 2;
    this.state.dog.y = spawnPt.y;

    this.setGameMasterComment(
      realMap.isRealOsm ? 'Bản Đồ Thực Tế OSM' : 'Mô Phỏng Vùng Miền',
      `[${realMap.label.split(',')[0]}] ${aiConditions?.gameTheoryNote || 'Địa bàn thực tế với mạng lưới đường sá thực!'}`,
      8000
    );
  }

  public setParanoia(value: number): void {
    this.state.paranoia = Math.max(0, Math.min(100, value));
  }

  private updateParanoia(dt: number): void {
    // Paranoia tự hạ nhiệt dần theo thời gian nếu xóm yên ắng
    if (this.state.paranoia > 0) {
      this.state.paranoia = Math.max(
        0,
        this.state.paranoia - GAME_CONFIG.gameTheory.dummyWalkers.paranoiaDecayRate * dt
      );
    }

    // Paranoia cao làm nón tầm nhìn của Bác tổ trưởng và radar quán nước mở rộng
    const paranoiaRatio = this.state.paranoia / 100;
    for (const npc of this.state.inspectors) {
      npc.range = 14 * (1 + paranoiaRatio * 0.45); // Tăng tới 20.3 mét
      npc.fov = ((90 + paranoiaRatio * 30) * Math.PI) / 180; // Mở rộng tới 120 độ
    }
    for (const stall of this.state.teaStalls) {
      stall.radius = 20 * (1 + paranoiaRatio * 0.4); // Tăng tới 28 mét radar
    }
  }

  private checkChickenGame(dt: number): void {
    const playerDog = this.state.dog;
    const player = this.state.player;

    for (const walker of this.state.dummyWalkers) {
      const dummyDog = walker.dog;
      if (!dummyDog) continue;
      const dist = Math.hypot(playerDog.x - dummyDog.x, playerDog.y - dummyDog.y);

      if (dist <= GAME_CONFIG.gameTheory.dummyWalkers.chickenGameDist) {
        walker.inChickenGameWithPlayer = true;

        // Trò chơi con gà: Hai con chó chạm mặt gầm gừ, thanh mót ruột tăng vọt!
        playerDog.bowels = Math.min(
          100,
          playerDog.bowels + GAME_CONFIG.gameTheory.dummyWalkers.chickenBowelsSurgeRate * dt
        );
        dummyDog.bowels = Math.min(
          100,
          dummyDog.bowels + GAME_CONFIG.gameTheory.dummyWalkers.chickenBowelsSurgeRate * dt
        );

        Haptics.leashTension();

        // Nếu người chơi hoảng sợ giật xích chạy trước -> Chó sủa ầm ĩ đánh động cả xóm!
        if (player.targetMove && !playerDog.isBarking) {
          playerDog.isBarking = true;
          playerDog.barkTimer = 2500;
          this.alertNearbyInspectors(playerDog.x, playerDog.y);
          this.showToast('🚨 Giật xích bỏ chạy! Chó bạn sủa inh ỏi đánh động Bác tổ trưởng!', 'danger');
        }
      } else if (dist > 4.5) {
        walker.inChickenGameWithPlayer = false;
      }
    }
  }

  private updateDummyWalkers(dt: number): void {
    const player = this.state.player;
    const playerDog = this.state.dog;
    const poopingWitnesses: DummyWalkerNPC[] = [];
    const defectWitnesses: DummyWalkerNPC[] = [];

    for (const walker of this.state.dummyWalkers) {
      // 1. Cập nhật bong bóng thoại & Cooldown
      if (walker.speechTimer && walker.speechTimer > 0) {
        walker.speechTimer -= dt * 1000;
        if (walker.speechTimer <= 0) {
          walker.speechText = undefined;
        }
      }
      if (walker.alarmCooldownTimer && walker.alarmCooldownTimer > 0) {
        walker.alarmCooldownTimer -= dt * 1000;
      }

      const dog = walker.dog;

      // 2. Chó đang xả phân (nếu có cún đi kèm)
      if (dog && dog.isPooping) {
        dog.poopTimer -= dt * 1000;
        if (dog.poopTimer <= 0) {
          dog.isPooping = false;
          dog.bowels = 0;

          // Thả bãi phân xuống đất
          const newPoop = {
            id: `poop-dummy-${Date.now()}-${walker.id}`,
            x: dog.x,
            y: dog.y,
            createdAt: Date.now(),
            ownerIsPlayer: false,
            isCleaned: false,
            isBlamed: false,
          };
          this.state.poops.push(newPoop);

          // Hành động theo Archetype Game Theory
          if (walker.archetype === 'altruist' || walker.archetype === 'ba_tam') {
            walker.isCleaning = true;
            walker.cleanTimer = 2500;
            walker.speechText = 'Dắt chó là phải mang túi dọn văn minh!';
            walker.speechTimer = 3500;
            walker.speechColor = '#00e676';
          } else if (walker.archetype === 'defector') {
            walker.isFleeing = true;
            walker.fleeTimer = 3500;
            walker.speechText = 'Chuồn lẹ không bị bóc phốt bây giờ!';
            walker.speechTimer = 3500;
            walker.speechColor = '#ff3d00';
            this.state.paranoia = Math.min(
              100,
              this.state.paranoia + GAME_CONFIG.gameTheory.dummyWalkers.paranoiaPerDefect
            );
            this.showToast(
              `⚠️ Kẻ lén lút vừa ỉa bậy bỏ chạy! Cảnh giác ngõ phố tăng lên ${Math.round(this.state.paranoia)}%!`,
              'danger'
            );
          } else if (walker.archetype === 'tit_for_tat') {
            if (walker.isSeekingRevenge) {
              walker.isSeekingRevenge = false;
              walker.hasWitnessedPlayerDefect = false;
              walker.speechText = 'Ăn miếng trả miếng nhé con vợ!';
              walker.speechTimer = 4000;
              walker.speechColor = '#ffd600';
              this.showToast('💩 Hùng Còi đã xả đúng 1 bãi trước cửa nhà bạn để trả thù!', 'danger');
            } else {
              walker.isCleaning = true;
              walker.cleanTimer = 2500;
              walker.speechText = 'Sống biết điều cho đời thanh thản.';
              walker.speechTimer = 3500;
              walker.speechColor = '#40c4ff';
            }
          }
        }
        continue;
      }

      // 3. Đang đứng hốt phân
      if (walker.isCleaning) {
        walker.cleanTimer -= dt * 1000;
        if (walker.cleanTimer <= 0) {
          walker.isCleaning = false;
          const latestPoop = this.state.poops
            .filter((p) => !p.ownerIsPlayer && !p.isCleaned)
            .pop();
          if (latestPoop) {
            latestPoop.isCleaned = true;
          }
        }
        continue;
      }

      // 4. Phản ứng chạy tới hiện trường tri hô (Dân Phòng / Tổ trưởng)
      let targetPoint = walker.patrolRoute[walker.currentRouteIndex] || { x: walker.x, y: walker.y };
      if (walker.isRushingToScene && walker.rushTarget) {
        targetPoint = walker.rushTarget;
        const distToScene = Math.hypot(walker.x - targetPoint.x, walker.y - targetPoint.y);
        if (distToScene < 1.5) {
          walker.isRushingToScene = false;
          walker.rushTarget = null;
          walker.speechText = 'Có mặt tại hiện trường! Dọn ngay không lập biên bản!';
          walker.speechTimer = 3500;
          walker.speechColor = '#2979ff';
        }
      } else if (walker.isSeekingRevenge && dog && dog.bowels >= 70) {
        targetPoint = { x: -40, y: 0 };
      }

      const dx = targetPoint.x - walker.x;
      const dy = targetPoint.y - walker.y;
      const dist = Math.hypot(dx, dy);

      let currentSpeed = walker.speed;
      if (walker.isFleeing || walker.isRushingToScene) {
        currentSpeed *= 1.7;
        if (walker.fleeTimer) {
          walker.fleeTimer -= dt * 1000;
          if (walker.fleeTimer <= 0) walker.isFleeing = false;
        }
      }

      if (dist < 0.6) {
        if (walker.isSeekingRevenge && dog && Math.hypot(walker.x - (-40), walker.y - 0) < 1.0) {
          dog.bowels = 100;
        } else if (walker.patrolRoute.length > 0) {
          walker.currentRouteIndex = (walker.currentRouteIndex + 1) % walker.patrolRoute.length;
        }
      } else {
        walker.facingAngle = Math.atan2(dy, dx);
        const moveStep = Math.min(dist, currentSpeed * dt);
        walker.x += (dx / dist) * moveStep;
        walker.y += (dy / dist) * moveStep;

        // Lưu vết chân cho chó bám theo (nếu có)
        if (dog) {
          dog.breadcrumbs.push({ x: walker.x, y: walker.y });
          if (dog.breadcrumbs.length > 15) {
            dog.breadcrumbs.shift();
          }
        }
      }

      // 5. Cập nhật vị trí chó của Walker (nếu có)
      if (dog) {
        const targetDogPos = dog.breadcrumbs[0] ?? {
          x: walker.x - Math.cos(walker.facingAngle) * 2.2,
          y: walker.y - Math.sin(walker.facingAngle) * 2.2,
        };
        const dogDx = targetDogPos.x - dog.x;
        const dogDy = targetDogPos.y - dog.y;
        const dogDist = Math.hypot(dogDx, dogDy);

        if (dogDist > 0.05) {
          dog.facingAngle = Math.atan2(dogDy, dogDx);
          const dogStep = Math.min(dogDist, currentSpeed * 1.05 * dt);
          dog.x += (dogDx / dogDist) * dogStep;
          dog.y += (dogDy / dogDist) * dogStep;
          dog.bowels = Math.min(100, dog.bowels + dogStep * 0.45);
        }

        if (dog.breadcrumbs.length > 0 && dogDist < 0.4) {
          dog.breadcrumbs.shift();
        }

        if (dog.bowels >= 100 && !dog.isPooping) {
          dog.isPooping = true;
          dog.poopTimer = 3000;
        }
      }

      // 6. Thu thập nhân chứng nhìn thấy cún hoặc người chơi
      if ((walker.alarmCooldownTimer ?? 0) <= 0) {
        // Kiểm tra thấy cún người chơi đang xả phân
        if (playerDog.isPooping) {
          const seesPooping = canSeeTarget(
            {
              x: walker.x,
              y: walker.y,
              facingAngle: walker.facingAngle,
              fov: walker.archetype === 'ba_tam' ? Math.PI * 0.65 : Math.PI * 0.45,
              range: 16,
            },
            { x: playerDog.x, y: playerDog.y },
            this.state.buildings
          );
          if (seesPooping) {
            poopingWitnesses.push(walker);
          }
        }

        // Kiểm tra thấy người chơi vừa xả phân xong định chuồn (activeDilemma)
        if (this.state.activeDilemma && this.state.activeDilemma.active) {
          const seesPlayer = canSeeTarget(
            {
              x: walker.x,
              y: walker.y,
              facingAngle: walker.facingAngle,
              fov: walker.archetype === 'ba_tam' || walker.archetype === 'dan_phong' ? Math.PI * 0.65 : Math.PI * 0.45,
              range: 18,
            },
            { x: player.x, y: player.y },
            this.state.buildings
          );
          if (seesPlayer) {
            defectWitnesses.push(walker);
          }
        }
      }
    }

    // 7. Bầu chọn Người Phát Ngôn Chính (Speaker Election qua SpeechCoordinator)
    if (defectWitnesses.length > 0) {
      const speaker = this.speechCoordinator.electAndSpeakGroup(
        defectWitnesses,
        'defect',
        { x: player.x, y: player.y },
        this.state.dummyWalkers,
        this.state.inspectors
      );

      if (speaker) {
        const reaction = handleNpcWitnessAction(speaker, 'defect', { x: player.x, y: player.y });
        if (reaction.soundWave) {
          this.state.soundWaves.push(reaction.soundWave);
        }
        if (reaction.paranoiaSurge) {
          this.state.paranoia = Math.min(100, this.state.paranoia + reaction.paranoiaSurge);
        }
        if (reaction.reputationPenalty) {
          this.state.reputation = Math.max(0, this.state.reputation - reaction.reputationPenalty);
        }

        // Nếu là Bà Tám hoặc Dân Phòng đứng sát (< 12m) -> Bắt quả tang ngay lập tức!
        if (
          (speaker.archetype === 'ba_tam' || speaker.archetype === 'dan_phong') &&
          Math.hypot(speaker.x - player.x, speaker.y - player.y) <= 12
        ) {
          this.triggerCaught(
            `${speaker.name} tận mắt rọi đèn bắt quả tang bạn dắt chó ỉa bậy không dọn!`,
            speaker.name,
            3.2 + reaction.disgraceMultiplierBonus
          );
        }
      }
    } else if (poopingWitnesses.length > 0) {
      this.speechCoordinator.electAndSpeakGroup(
        poopingWitnesses,
        'pooping',
        { x: playerDog.x, y: playerDog.y },
        this.state.dummyWalkers,
        this.state.inspectors
      );
    }
  }

  private updateSoundWaves(dt: number): void {
    if (!this.state.soundWaves || this.state.soundWaves.length === 0) return;

    for (const wave of this.state.soundWaves) {
      wave.radius += wave.speed * dt;
      wave.lifeTimer -= dt * 1000;

      // Lan truyền sóng âm: Báo động cho các Dân Phòng hoặc Hàng Xóm gần đó
      const alertCandidates: DummyWalkerNPC[] = [];
      for (const walker of this.state.dummyWalkers) {
        const d = Math.hypot(walker.x - wave.x, walker.y - wave.y);
        if (d <= wave.radius && d >= wave.radius - 8) {
          if (walker.archetype === 'dan_phong' && !walker.isRushingToScene) {
            walker.isRushingToScene = true;
            walker.rushTarget = { x: wave.x, y: wave.y };
          }
          alertCandidates.push(walker);
        }
      }

      if (alertCandidates.length > 0) {
        this.speechCoordinator.handleSoundWaveHeard(
          alertCandidates,
          { x: wave.x, y: wave.y },
          this.state.dummyWalkers,
          this.state.inspectors
        );
      }

      // Inspectors (Bác Tổ Trưởng)
      const hearingInspectors: InspectorNPC[] = [];
      for (const inspector of this.state.inspectors) {
        const d = Math.hypot(inspector.x - wave.x, inspector.y - wave.y);
        if (d <= wave.radius) {
          inspector.facingAngle = Math.atan2(wave.y - inspector.y, wave.x - inspector.x);
          hearingInspectors.push(inspector);
        }
      }
      if (hearingInspectors.length > 0 && this.speechCoordinator.canTriggerEvent('inspector_wave')) {
        const chosen = hearingInspectors[0]!;
        const inspectorLines = [
          'Ai tri hô thế? Để tôi ra xem!',
          'Có biến ngoài ngõ, ra kiểm tra ngay!',
          'Mất trật tự quá, ai làm gì đấy!',
        ];
        chosen.speechText = inspectorLines[Math.floor(Math.random() * inspectorLines.length)]!;
        chosen.speechTimer = 3500;
        this.speechCoordinator.setContextCooldown('inspector_wave', 2200);

        for (let i = 1; i < hearingInspectors.length; i++) {
          if (!hearingInspectors[i]!.speechText) {
            hearingInspectors[i]!.speechText = '❓';
            hearingInspectors[i]!.speechTimer = 1500;
          }
        }
      }
    }

    // Xóa các sóng âm đã hết hạn
    this.state.soundWaves = this.state.soundWaves.filter(
      (w) => w.lifeTimer > 0 && w.radius < w.maxRadius
    );
  }

  public resetRound(): void {
    this.state.caughtEvent = null;

    // Luôn chọn điểm xuất phát thuộc về đường gần tâm bản đồ nhất, không bao giờ lơ lửng ở đất trống
    let spawnX = -40;
    let spawnY = 0;
    if (this.state.roads && this.state.roads.length > 0) {
      const centerSnap = snapToRoad({ x: 0, y: 0 }, this.state.roads);
      spawnX = centerSnap.snapped.x;
      spawnY = centerSnap.snapped.y;
    }

    this.state.player.x = spawnX;
    this.state.player.y = spawnY;
    this.state.player.isEntangled = false;
    this.state.player.isCleaning = false;
    this.state.player.speed = GAME_CONFIG.resources.moveSpeed;
    this.state.player.targetMove = null;
    this.state.player.waypoints = [];
    const dogSnap = snapToRoad({ x: spawnX - 1.5, y: spawnY }, this.state.roads);
    this.state.dog.x = dogSnap.snapped.x;
    this.state.dog.y = dogSnap.snapped.y;
    this.state.dog.bowels = 10;
    this.state.dog.stress = 0;
    this.state.dog.isPooping = false;
    this.state.dog.isBarking = false;
    this.state.dog.breadcrumbs = [{ x: dogSnap.snapped.x, y: dogSnap.snapped.y }];
    this.lastBreadcrumbPos = { x: spawnX, y: spawnY };
    this.state.reputation = 100;
    this.state.bagsCount = GAME_CONFIG.resources.maxBagsPerWalk;
    this.state.activeDilemma = null;
    this.state.qteKick.active = false;
    this.state.paranoia = 10;
    this.state.soundWaves = [];
    this.showToast('Bắt đầu ván dắt chó mới!', 'info');
  }

  public triggerShoutAlarm(x: number, y: number, text: string, maxRadius = 25): void {
    this.state.soundWaves.push({
      id: `promo-wave-${Date.now()}`,
      x,
      y,
      radius: 2,
      maxRadius,
      speed: 12,
      lifeTimer: 3000,
      sourceName: 'Bà Tám Đầu Ngõ',
      color: 'rgba(255, 215, 0, 0.75)',
      shoutText: text,
    });
  }

  public triggerViralBusted(reason: string, caughtBy: string, mult = 3.5): void {
    this.triggerCaught(reason, caughtBy, mult);
  }
}

